import axios from 'axios';
import posthog from 'posthog-js';
import { MutableRefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { toast } from 'react-toastify';

import { useAppDispatch } from '../../hooks/redux';
import { usePosthogCapture } from '../../hooks/usePosthogCapture';
import { socket } from '../../libs/socket';
import { baseUrl, consultationServiceConsultationsPath } from '../../configs/api/api.config';
import { setActiveTool } from '../../store/meetActiveTool.slice';
import { store } from '../../store/store';
import { IClient, IConsultation, IJournalResult, IUser } from '../../store/types';
import { getOrCreateGroupClientId } from '../../utils/groupMeeting';

interface UseMeetSessionArgs {
  roomName: string;
  consultation?: IConsultation;
  client?: IClient;
  userData?: IUser | null;
  isPsychologist: boolean;
  isGroupConsultationFlag: boolean;
  destroyed: boolean;
  videoRoomInitialized: boolean | null;
  joinRoom: (roomName: string, displayName: string, userId: string, role: string) => Promise<unknown>;
  leaveRoom: () => void;
  callDestroyRoom: () => Promise<void>;
  getRoomId: (roomName: string) => Promise<unknown>;
  getRoomInfo: (roomId: string) => Promise<unknown[]>;
  initializeConnection: (identity: string, isCameraEnabled?: boolean) => Promise<void>;
  refreshPermissions: () => Promise<void>;
  navigate: (path: string) => void;
  result: IJournalResult;
  setResult: (value: IJournalResult) => void;
  isResultInitialized: boolean;
  setIsResultInitialized: (value: boolean) => void;
  groupParticipantNamesRef: MutableRefObject<Record<string, string>>;
  macClientSelections: Record<string, unknown>;
  emotionsClientSelections: Record<string, unknown>;
}

function toSortedObject<T>(source: Record<string, T>) {
  return Object.fromEntries(
    Object.entries(source)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, value]) => [key, value]),
  );
}

const createEmptyJournalResult = (): IJournalResult => ({
  questions: [],
  dpdg: { date_time: '', comment: '' },
  emotions: [],
  mac_cards: [],
  wheel: [],
  color_code: [],
  clouds: [],
  result: '',
});

export function useMeetSession({
  roomName,
  consultation,
  client,
  userData,
  isPsychologist,
  isGroupConsultationFlag,
  destroyed,
  videoRoomInitialized,
  joinRoom,
  leaveRoom,
  callDestroyRoom,
  getRoomId,
  getRoomInfo,
  initializeConnection,
  refreshPermissions,
  navigate,
  result,
  setResult,
  isResultInitialized,
  setIsResultInitialized,
  groupParticipantNamesRef,
  macClientSelections,
  emotionsClientSelections,
}: UseMeetSessionArgs) {
  const dispatch = useAppDispatch();
  const captureEvent = usePosthogCapture();
  const clientIdentityRef = useRef<number | null>(null);
  const sessionStartedRef = useRef(false);
  const sessionStartTimestampRef = useRef<number | null>(null);
  const roomIdRef = useRef<unknown>(null);
  const lastSaveSignatureRef = useRef<string | null>(null);

  const [started, setStarted] = useState(false);
  const [users, setUsers] = useState<unknown[]>([]);
  const [isInitialVideoMuted, setIsInitialVideoMuted] = useState(false);

  const participantRole = userData?.id ? 'psychologist' : 'client';
  const parsedClientId = useMemo(() => {
    const pathId = consultation?.client_entity?.split('/').at(-1);

    if (!pathId) {
      return null;
    }

    const numericId = Number(pathId);
    return Number.isNaN(numericId) ? null : numericId;
  }, [consultation?.client_entity]);

  const sessionCommonProps = useMemo(
    () => ({
      consultation_id: consultation?.id ?? null,
      consultation_code: consultation?.code ?? null,
      room_code: roomName,
      scheduled_start: consultation?.datetime_start ?? null,
      scheduled_end: consultation?.datetime_end ?? null,
      psychologist_id: consultation?.psychologist?.id ?? null,
      client_id: client?.id ?? parsedClientId,
    }),
    [
      client?.id,
      consultation?.code,
      consultation?.datetime_end,
      consultation?.datetime_start,
      consultation?.id,
      consultation?.psychologist?.id,
      parsedClientId,
      roomName,
    ],
  );

  const captureSessionEvent = useCallback(
    (eventName: string, properties: Record<string, unknown>) => {
      const payload = {
        ...sessionCommonProps,
        participant_role: participantRole,
        ...properties,
      };

      if (userData?.id) {
        captureEvent(eventName, payload);
      } else {
        posthog.capture(eventName, payload);
      }
    },
    [captureEvent, participantRole, sessionCommonProps, userData?.id],
  );

  const handleToolActivated = useCallback(
    (toolId: string, properties: Record<string, unknown> = {}) => {
      captureSessionEvent('session_tool_launched', {
        tool_id: toolId,
        ...properties,
      });
    },
    [captureSessionEvent],
  );

  const emitSessionEnded = useCallback(() => {
    if (!sessionStartedRef.current) {
      return;
    }

    const endedAt = Date.now();
    const startedAt = sessionStartTimestampRef.current;
    const durationSeconds = startedAt ? Math.max(Math.round((endedAt - startedAt) / 1000), 0) : null;

    captureSessionEvent('session_ended', {
      ended_at: new Date(endedAt).toISOString(),
      started_at: startedAt ? new Date(startedAt).toISOString() : null,
      duration_seconds: durationSeconds,
    });

    sessionStartedRef.current = false;
    sessionStartTimestampRef.current = null;
  }, [captureSessionEvent]);

  const saveConsultationResult = useCallback(async () => {
    const getToken = () => localStorage.getItem('tokensZZ');

    if (!consultation || !isPsychologist || !isResultInitialized) {
      return;
    }

    const baseSignaturePayload: Record<string, unknown> = {
      consultationId: consultation.id,
      result,
      isGroupConsultationFlag,
    };
    let payload: IJournalResult = { ...result };

    if (isGroupConsultationFlag) {
      const state = store.getState();
      const macByParticipant = state.macStateSlice?.clientSelectionsByParticipant ?? {};
      const emotionsByParticipant = state.emotionsGroupSlice?.clientSelectionsByParticipant ?? {};
      const participantNames = groupParticipantNamesRef.current ?? {};
      baseSignaturePayload.macByParticipant = toSortedObject(macByParticipant);
      baseSignaturePayload.emotionsByParticipant = toSortedObject(emotionsByParticipant);
      baseSignaturePayload.groupParticipantNames = toSortedObject(participantNames);

      const mac_cards_by_participant: Record<string, { date_time: string; cards: string[]; comment: string }[]> = {};

      for (const [identity, cards] of Object.entries(macByParticipant)) {
        if (cards?.length) {
          mac_cards_by_participant[identity] = [
            { date_time: new Date().toISOString(), cards: cards.map((card: { img: string }) => card.img), comment: '' },
          ];
        }
      }

      const emoToolSettings =
        store.getState().psyToolsSlice?.tools?.find((tool: { name: string }) => tool.name === 'emotions_detection')
          ?.psy_tool_settings ?? [];

      const emotions_by_participant: Record<
        string,
        { date_time: string; comment: string; emotions: { category: string; items: string[] }[] }[]
      > = {};

      for (const [identity, selection] of Object.entries(emotionsByParticipant)) {
        if (selection?.emo?.length) {
          const emotionsByCategory = (selection.category ?? [])
            .map((categoryName: string) => {
              const categoryItems: string[] =
                (
                  emoToolSettings.find((setting: { name: string }) => setting.name === categoryName) as
                    | { value?: string[] }
                    | undefined
                )?.value ?? [];
              const items = categoryItems.filter((item: string) => selection.emo.includes(item));

              return { category: categoryName, items };
            })
            .filter((emotion: { items: string[] }) => emotion.items.length > 0);

          emotions_by_participant[identity] = [
            {
              date_time: new Date().toISOString(),
              comment: '',
              emotions:
                emotionsByCategory.length > 0 ? emotionsByCategory : [{ category: 'Эмоции', items: selection.emo }],
            },
          ];
        }
      }

      if (Object.keys(mac_cards_by_participant).length > 0 || Object.keys(emotions_by_participant).length > 0) {
        payload = {
          ...payload,
          mac_cards_by_participant: Object.keys(mac_cards_by_participant).length
            ? mac_cards_by_participant
            : payload.mac_cards_by_participant,
          emotions_by_participant: Object.keys(emotions_by_participant).length
            ? emotions_by_participant
            : payload.emotions_by_participant,
          group_participant_names: Object.keys(participantNames).length
            ? participantNames
            : payload.group_participant_names,
        };
      }
    }

    const saveSignature = JSON.stringify(baseSignaturePayload);
    if (lastSaveSignatureRef.current === saveSignature) {
      return;
    }
    lastSaveSignatureRef.current = saveSignature;

    try {
      await axios.put(
        `${baseUrl}${consultationServiceConsultationsPath}/${consultation.id}`,
        { result: [payload] },
        {
          headers: {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            Authorization: `Bearer ${JSON.parse(getToken()).token}`,
          },
        },
      );
    } catch (error) {
      // Позволяем ретрай при следующем триггере с теми же данными.
      lastSaveSignatureRef.current = null;
      throw error;
    }
  }, [consultation, groupParticipantNamesRef, isGroupConsultationFlag, isPsychologist, isResultInitialized, result]);

  const closeMeet = useCallback(async () => {
    localStorage.removeItem('roomName');
    emitSessionEnded();

    if (!isPsychologist) {
      leaveRoom();

      socket.emit('message', {
        roomId: roomName,
        message: {
          datetime: new Date().toISOString(),
          action: 'close_tools',
        },
      });
    } else {
      await callDestroyRoom();
      void saveConsultationResult();
    }

    navigate(`/end-call/${roomName}`);
  }, [callDestroyRoom, emitSessionEnded, isPsychologist, leaveRoom, navigate, roomName, saveConsultationResult]);

  const leaveConferenceOnly = useCallback(() => {
    localStorage.removeItem('roomName');
    emitSessionEnded();
    leaveRoom();
    navigate(`/end-call/${roomName}`);
  }, [emitSessionEnded, leaveRoom, navigate, roomName]);

  const startWithMedia = useCallback(
    async (value: boolean, isCameraEnabled = true) => {
      if (value) {
        try {
          const groupClientIdentity = getOrCreateGroupClientId(roomName).identity;
          const connectionIdentity = isPsychologist
            ? `${consultation?.psychologist.first_name} - psychologist`
            : isGroupConsultationFlag
              ? groupClientIdentity
              : client?.full_name
                ? `${client.full_name} - client`
                : client?.id
                  ? `client_${client.id}`
                  : 'client';

          await initializeConnection(connectionIdentity, isCameraEnabled);
          await refreshPermissions();
        } catch (error) {
          console.error('Failed to start media and connect to room', error);
          toast.error('Не удалось подготовить аудио и видео для подключения.');
          return;
        }
      }

      setIsInitialVideoMuted(!isCameraEnabled);
      setStarted(value);
    },
    [
      client?.full_name,
      consultation?.psychologist.first_name,
      initializeConnection,
      isGroupConsultationFlag,
      isPsychologist,
      refreshPermissions,
      roomName,
    ],
  );

  useEffect(() => {
    setResult(createEmptyJournalResult());
    setIsResultInitialized(false);
  }, [consultation?.id, setIsResultInitialized, setResult]);

  useEffect(() => {
    if (!consultation?.id || isResultInitialized) {
      return;
    }

    const persistedResult = consultation.result?.[0];

    if (persistedResult) {
      setResult(persistedResult);
    }

    setIsResultInitialized(true);
  }, [consultation?.id, consultation?.result, isResultInitialized, setIsResultInitialized, setResult]);

  useEffect(() => {
    lastSaveSignatureRef.current = null;
  }, [consultation?.id]);

  useEffect(() => {
    if (!isGroupConsultationFlag || !consultation?.id || isResultInitialized) {
      return;
    }

    const persistedResult = consultation.result?.[0];
    setResult(persistedResult ? { ...persistedResult } : createEmptyJournalResult());
    setIsResultInitialized(true);
  }, [
    consultation?.id,
    consultation?.result,
    isGroupConsultationFlag,
    isResultInitialized,
    setIsResultInitialized,
    setResult,
  ]);

  useEffect(() => {
    if (!client?.id || userData?.id) {
      return;
    }

    if (clientIdentityRef.current === client.id) {
      return;
    }

    const distinctId = `client_${client.id}`;

    posthog.identify(distinctId, {
      role: 'client',
      client_id: client.id,
      name: client.full_name || undefined,
      consultation_code: consultation?.code ?? undefined,
    });
    posthog.group('user_role', 'client', { role: 'client', consultation_code: consultation?.code ?? undefined });

    clientIdentityRef.current = client.id;
  }, [client?.full_name, client?.id, consultation?.code, userData?.id]);

  useEffect(() => {
    if (!started || sessionStartedRef.current) {
      return;
    }

    const startedAt = Date.now();
    sessionStartedRef.current = true;
    sessionStartTimestampRef.current = startedAt;

    captureSessionEvent('session_started', {
      started_at: new Date(startedAt).toISOString(),
    });
  }, [captureSessionEvent, started]);

  useEffect(() => {
    if (started) {
      return;
    }

    emitSessionEnded();
  }, [emitSessionEnded, started]);

  useEffect(() => {
    if (!started) {
      return;
    }

    const userId = userData?.id?.toString() || 'unknown';
    const role = isPsychologist ? 1 : 2;
    const groupClientDisplayName = getOrCreateGroupClientId(roomName).displayName;
    const displayName = isPsychologist
      ? `${consultation?.psychologist.first_name} - psychologist`
      : isGroupConsultationFlag
        ? groupClientDisplayName
        : client?.full_name
          ? `${client.full_name} - client`
          : 'Клиент - client';

    joinRoom(roomName, displayName, userId, String(role))
      .then(() => {
        socket.emit('join', roomName);
      })
      .catch((error) => {
        console.error('Failed to join room', error);
        setStarted(false);
      });
  }, [
    client?.full_name,
    consultation?.psychologist.first_name,
    isGroupConsultationFlag,
    isPsychologist,
    joinRoom,
    roomName,
    started,
    userData?.id,
  ]);

  useEffect(() => {
    if (!videoRoomInitialized) {
      return;
    }

    const updateActiveUsers = (nextUsers: unknown[]) => {
      setUsers((prevUsers) => {
        if (JSON.stringify(prevUsers) !== JSON.stringify(nextUsers)) {
          return nextUsers;
        }

        return prevUsers;
      });
    };

    const timer = setInterval(() => {
      if (roomIdRef.current == null) {
        void getRoomId(roomName).then((id) => {
          if (id) {
            roomIdRef.current = id;
            void getRoomInfo(roomIdRef.current).then((activeUsers) => {
              updateActiveUsers(activeUsers || []);
            });
          }
        });
        return;
      }

      void getRoomInfo(roomIdRef.current).then((activeUsers) => {
        updateActiveUsers(activeUsers || []);
      });
    }, 1000);

    return () => {
      clearInterval(timer);
    };
  }, [getRoomId, getRoomInfo, roomName, videoRoomInitialized]);

  useEffect(() => {
    if (users.length === 1) {
      dispatch(setActiveTool(0));
      socket.emit('message', {
        roomId: roomName,
        message: {
          datetime: new Date().toISOString(),
          action: 'close_tools',
        },
      });
    }
  }, [dispatch, roomName, users.length]);

  useLayoutEffect(() => {
    if (destroyed && !isPsychologist) {
      navigate(`/end-call/${roomName}`);
    }
  }, [destroyed, isPsychologist, navigate, roomName]);

  useEffect(() => {
    if (!isResultInitialized) {
      return;
    }

    void saveConsultationResult();
  }, [consultation, isGroupConsultationFlag, isPsychologist, isResultInitialized, result, saveConsultationResult]);

  useEffect(() => {
    if (!isGroupConsultationFlag || !isPsychologist || !isResultInitialized) {
      return;
    }

    const hasMacData = Object.keys(macClientSelections).length > 0;
    const hasEmotionsData = Object.keys(emotionsClientSelections).length > 0;

    if (!hasMacData && !hasEmotionsData) {
      return;
    }

    void saveConsultationResult();
  }, [
    emotionsClientSelections,
    isGroupConsultationFlag,
    isPsychologist,
    isResultInitialized,
    macClientSelections,
    saveConsultationResult,
  ]);

  useEffect(() => {
    const setVhProperty = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setVhProperty();
    window.addEventListener('resize', setVhProperty);

    return () => {
      window.removeEventListener('resize', setVhProperty);
    };
  }, []);

  useEffect(() => () => emitSessionEnded(), [emitSessionEnded]);

  return {
    started,
    users,
    isInitialVideoMuted,
    isClientConnected: users.length > 1,
    startWithMedia,
    closeMeet,
    leaveConferenceOnly,
    handleToolActivated,
    emitSessionEnded,
    saveConsultationResult,
  };
}
